<script setup lang="ts">
const props = defineProps<{
  onClick: (call: any) => Promise<void>;
  title: string;
  loadingTitle: string;
  loading: boolean;
  error: boolean;
  errorMessage: string;
}>();

const isLoading = ref(false);
const isError = ref(false);

async function handleClick(call: any) {
  // console.log("handleClick");
  try {
    await props.onClick(call);
    isLoading.value = props.loading;
  } catch (error) {
    isError.value = true;
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <button
    class="custom-button"
    :class="{ 'error-button': props.error }"
    @click="handleClick($event)"
  >
    <div v-if="!props.loading && !props.error">
      {{ props.title }}
    </div>
    <div v-if="props.loading" class="flex items-center justify-center">
      <!-- Add loading spinner using native CSS -->
      <span class="loading loading-spinner mr-3" />
      <p>{{ props.loadingTitle }}</p>
    </div>
    <div v-if="props.error" class="flex items-center justify-center">
      <!-- Add error icon using native CSS -->
      <span class="error-icon mr-3" />
      <p>{{ props.errorMessage }}</p>
    </div>
  </button>
</template>

<style scoped>
.custom-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px 16px;
  font-weight: 500;
  font-size: 1rem;
  color: white;
  background-color: #1a2b55;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  box-shadow: 0 4px 0 0 #14213d, 0 5px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(0);
  overflow: hidden;
}

.custom-button:before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0) 100%
  );
  border-radius: 8px;
}

.custom-button:hover {
  background-color: #243670;
  transform: translateY(-2px);
  box-shadow: 0 6px 0 0 #14213d, 0 7px 10px rgba(0, 0, 0, 0.2);
}

.custom-button:active {
  transform: translateY(2px);
  box-shadow: 0 2px 0 0 #14213d, 0 3px 6px rgba(0, 0, 0, 0.1);
}

.error-button {
  background-color: #e53935;
  box-shadow: 0 4px 0 0 #b71c1c, 0 5px 8px rgba(0, 0, 0, 0.15);
}

.error-button:hover {
  background-color: #f44336;
  box-shadow: 0 6px 0 0 #b71c1c, 0 7px 10px rgba(0, 0, 0, 0.2);
}

.error-button:active {
  box-shadow: 0 2px 0 0 #b71c1c, 0 3px 6px rgba(0, 0, 0, 0.1);
}

.loading-spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-left-color: #ffffff;
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

.error-icon {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid transparent;
  border-color: #fff;
  border-radius: 50%;
  background-color: transparent;
  position: relative;
}

.error-icon:before,
.error-icon:after {
  content: "";
  display: block;
  position: absolute;
  width: 2px;
  height: 0.5rem;
  background-color: #fff;
  top: 50%;
  left: 50%;
}

.error-icon:before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.error-icon:after {
  transform: translate(-50%, -50%) rotate(-45deg);
}
</style>
